import { useEffect, useMemo, useState } from "react";
import { DateTimePicker } from "../components/DateTimePicker";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Phone, Mail, Check, X, Clock, Video, Copy, CalendarPlus, Sparkles, UserPlus, Loader2, ExternalLink } from "lucide-react";

const STATUS = {
  confirmed: { label: "Confirmed", cls: "bg-[#C8FF4D] text-[#0B0D10] border-[#B8EF3D]" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  no_show:   { label: "No-show",   cls: "bg-amber-100 text-amber-900 border-amber-200" },
  cancelled: { label: "Cancelled", cls: "bg-[#EFEBE1] text-[#25282E]/60 border-[#E4E0D6]" },
};

const inputCls = "w-full rounded-xl bg-white border border-[#E4E0D6] focus:border-[#0B0D10] outline-none px-3.5 h-11 text-[13.5px] transition-colors";
const labelCls = "block text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#3A3D44]/60 mb-1.5";

function BookingForm({ onBooked }) {
  const [f, setF] = useState({ business: "", name: "", email: "", phone: "", date: "", time: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business || !f.name || !f.email || !f.date || !f.time) { toast.error("Fill in the business, contact, email and time"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/staff/bookings", {
        business: f.business, name: f.name, email: f.email, phone: f.phone || null,
        when_local: `${f.date}T${f.time}`, notes: f.notes || null,
      });
      setConfirmed(data);
      onBooked?.();
      toast.success("Call booked — invites are on their way");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Meeting link copied"); } catch (_) {}
  };

  if (confirmed) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl bg-[#0B0D10] text-white p-7 relative overflow-hidden grain" data-testid="booking-confirmed-card">
        <div className="absolute -top-14 -right-14 h-48 w-48 rounded-full bg-[#C8FF4D]/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="h-12 w-12 rounded-full bg-[#C8FF4D] text-[#0B0D10] grid place-items-center mb-5"><Check size={24} strokeWidth={3} /></div>
          <h3 className="font-display font-extrabold text-[22px] tracking-[-0.02em]">Booked.</h3>
          <p className="text-white/60 text-[13.5px] mt-1.5 leading-relaxed">
            {confirmed.slot_label} · booked by {confirmed.booked_by}.{" "}
            {confirmed.emails_sent ? "Calendar invites emailed to the prospect and the BackPocket inbox." : "Saved — but the emails failed to send, share the link manually."}
          </p>
          <div className="mt-5 rounded-2xl bg-white/[0.06] border border-white/12 p-4">
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/50 font-semibold mb-2">Video call link</div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-white/85 font-mono truncate flex-1">{confirmed.meeting_url}</span>
              <button onClick={() => copyLink(confirmed.meeting_url)} data-testid="booking-copy-link"
                className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center shrink-0 transition-colors"><Copy size={13} /></button>
              <a href={confirmed.meeting_url} target="_blank" rel="noreferrer" data-testid="booking-join-link"
                className="btn-lift h-9 px-4 rounded-full bg-[#C8FF4D] text-[#0B0D10] text-[12.5px] font-bold inline-flex items-center gap-1.5 shrink-0"><Video size={13} strokeWidth={2.4} />Join</a>
            </div>
          </div>
          <button onClick={() => { setConfirmed(null); setF({ business: "", name: "", email: "", phone: "", date: "", time: "", notes: "" }); }}
            data-testid="booking-book-another"
            className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 hover:text-white transition-colors">
            <CalendarPlus size={14} />Book another call
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="rounded-3xl bg-white border border-[#E8E5DE] p-6 sm:p-7 shadow-sm" data-testid="booking-form-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-2xl bg-[#0B0D10] grid place-items-center"><Video size={17} className="text-[#C8FF4D]" /></div>
        <div>
          <h3 className="font-display font-bold text-[17px] tracking-[-0.01em]">Book a 15-min call</h3>
          <p className="text-[12px] text-[#3A3D44]/60">Video link + calendar invite sent to everyone automatically</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Business name</label>
          <input data-testid="booking-business" value={f.business} onChange={set("business")} placeholder="e.g. Rosie's Espresso" className={inputCls} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Contact name</label>
            <input data-testid="booking-contact" value={f.name} onChange={set("name")} placeholder="Who's attending" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone <span className="normal-case font-normal tracking-normal">(gets a reminder text 1h before)</span></label>
            <input data-testid="booking-phone" value={f.phone} onChange={set("phone")} placeholder="04xx xxx xxx" className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="booking-email" type="email" value={f.email} onChange={set("email")} placeholder="Their invite lands here" className={inputCls} />
        </div>
        <DateTimePicker date={f.date} time={f.time} onChange={({ date, time }) => setF((s) => ({ ...s, date, time }))} />
        <div>
          <label className={labelCls}>Notes / call goals <span className="normal-case font-normal tracking-normal">(optional)</span></label>
          <textarea data-testid="booking-notes" value={f.notes} onChange={set("notes")} placeholder="What they're after, objections, anything useful…"
            className="w-full rounded-xl bg-white border border-[#E4E0D6] focus:border-[#0B0D10] outline-none p-3 text-[13.5px] transition-colors min-h-[80px]" />
        </div>
        <button type="submit" disabled={busy} data-testid="booking-submit-button"
          className="btn-lift w-full h-12 rounded-full bg-[#0B0D10] text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2 hover:bg-black transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <CalendarPlus size={15} strokeWidth={2.2} />}
          {busy ? "Booking…" : "Book the call & send invites"}
        </button>
      </form>
    </div>
  );
}

function ProspectQuickCreate({ onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post("/staff/prospects", { business_name: name.trim() });
      toast.success(`${name.trim()} added to the pipeline`);
      setName(""); onCreated?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl bg-[#EFEBE1] border border-[#E3DFD4] p-6" data-testid="prospect-create-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-2xl bg-white grid place-items-center"><Sparkles size={16} className="text-[#0B0D10]" /></div>
        <div>
          <h3 className="font-display font-bold text-[15.5px] tracking-[-0.01em]">Add a prospect</h3>
          <p className="text-[12px] text-[#3A3D44]/60">Drop them in the pipeline before the call</p>
        </div>
      </div>
      <form onSubmit={create} className="flex gap-2">
        <input data-testid="prospect-name-input" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Business name" className={inputCls} />
        <button type="submit" disabled={busy || name.trim().length < 2} data-testid="prospect-create-button"
          className="btn-lift h-11 px-4 rounded-xl bg-[#0B0D10] text-white text-[13px] font-semibold shrink-0 disabled:opacity-40 inline-flex items-center gap-1.5">
          <UserPlus size={13} strokeWidth={2.4} />Add
        </button>
      </form>
    </div>
  );
}

export default function SalesDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [filter, setFilter] = useState("upcoming");

  const load = () => {
    api.get("/staff/bookings").then((r) => setRows(r.data.bookings || [])).catch(() => setRows([]));
    api.get("/staff/prospects").then((r) => setProspects(r.data.prospects || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const now = Date.now();
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    if (filter === "all") return rows;
    if (filter === "past") return rows.filter((b) => new Date(b.slot_at).getTime() < now || ["completed", "no_show"].includes(b.status));
    if (filter === "today") return rows.filter((b) => { const t = new Date(b.slot_at).getTime(); return t >= startOfDay.getTime() && t < startOfDay.getTime() + 86400000 && b.status !== "cancelled"; });
    return rows.filter((b) => new Date(b.slot_at).getTime() >= now && b.status === "confirmed");
  }, [rows, filter]);

  const counts = useMemo(() => {
    if (!rows) return { upcoming: 0, today: 0, closed: 0 };
    const now = Date.now();
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    return {
      upcoming: rows.filter((b) => new Date(b.slot_at).getTime() >= now && b.status === "confirmed").length,
      today: rows.filter((b) => { const t = new Date(b.slot_at).getTime(); return t >= startOfDay.getTime() && t < startOfDay.getTime() + 86400000 && b.status !== "cancelled"; }).length,
      closed: rows.filter((b) => b.status === "completed").length,
    };
  }, [rows]);

  const setStatus = async (b, status) => {
    try {
      await api.post(`/staff/bookings/${b.id}/status`, { status });
      toast.success(`Marked ${STATUS[status]?.label || status}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pipeline = prospects.filter((p) => p.pipeline_status === "prospect");

  return (
    <div data-testid="sales-desk">
      <PageHeader title="Sales desk" subtitle={`Book calls, run the pipeline, close. Signed in as ${user?.name || "staff"}.`} />

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3.5 mb-8">
        {[
          { label: "Upcoming calls", value: counts.upcoming, tone: "ink" },
          { label: "Today", value: counts.today, tone: "lime" },
          { label: "Calls closed", value: counts.closed, tone: "ink" },
        ].map(({ label, value, tone }) => (
          <div key={label} className={`rounded-2xl p-5 ${tone === "ink" ? "bg-[#0B0D10] text-white" : "bg-[#C8FF4D] text-[#0B0D10]"}`} data-testid={`sales-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="font-display font-extrabold tracking-[-0.02em] text-[30px] leading-none tabular-nums">{value}</div>
            <div className={`text-[10.5px] uppercase tracking-[0.14em] mt-2.5 font-semibold ${tone === "ink" ? "text-white/50" : "text-[#0B0D10]/60"}`}>{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-6 items-start">
        {/* Left rail: booking form + prospect creator */}
        <div className="space-y-5">
          <BookingForm onBooked={load} />
          <ProspectQuickCreate onCreated={load} />
          {pipeline.length > 0 && (
            <div className="rounded-3xl bg-white border border-[#E8E5DE] p-6" data-testid="prospect-pipeline">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-[15.5px] tracking-[-0.01em]">Pipeline</h3>
                <span className="text-[11px] font-bold uppercase tracking-[0.12em] bg-[#6474FF]/12 text-[#4353E8] px-2.5 py-1 rounded-full">{pipeline.length} prospects</span>
              </div>
              <ul className="space-y-1">
                {pipeline.slice(0, 8).map((p) => (
                  <li key={p.id} className="flex items-center gap-3 py-2 px-2.5 -mx-2.5 rounded-xl hover:bg-[#FAF7F0] transition-colors" data-testid={`pipeline-${p.id}`}>
                    <span className="h-8 w-8 rounded-lg bg-[#0B0D10] text-[#C8FF4D] font-display font-extrabold grid place-items-center text-[11px] shrink-0">
                      {(p.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                    <span className="text-[13.5px] font-medium truncate">{p.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: meetings list */}
        <div>
          <div className="flex items-center gap-1.5 rounded-full bg-[#F0EDE5] border border-[#E8E5DE] p-1 w-fit mb-4">
            {[["upcoming", "Upcoming"], ["today", "Today"], ["past", "Past"], ["all", "All"]].map(([id, label]) => (
              <button key={id} data-testid={`sales-filter-${id}`} onClick={() => setFilter(id)}
                className={`px-4 h-8 rounded-full text-[12.5px] font-medium transition-colors ${filter === id ? "bg-white text-[#0B0D10] shadow-sm" : "text-[#3A3D44]/60 hover:text-[#0B0D10]"}`}>
                {label}
              </button>
            ))}
          </div>

          {filtered === null ? (
            <div className="text-[13px] text-[#3A3D44]/60 animate-pulse">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-[#E8E5DE] p-14 text-center bg-white/40" data-testid="sales-empty">
              <Video size={22} className="mx-auto text-[#3A3D44]/40 mb-3" />
              <h3 className="font-display font-bold text-lg tracking-[-0.01em]">No {filter === "all" ? "" : filter} calls.</h3>
              <p className="text-[13px] text-[#3A3D44]/60 mt-1.5">Book one on the left — invites go out the second you hit save.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="sales-meetings-list">
              <AnimatePresence>
                {filtered.map((b, i) => (
                  <motion.li key={b.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.3) }}
                    className="rounded-2xl bg-white border border-[#E8E5DE] p-5 hover:border-[#0B0D10]/25 transition-colors" data-testid={`meeting-${b.id}`}>
                    <div className="flex flex-wrap sm:flex-nowrap items-start gap-4">
                      <div className="h-11 w-11 rounded-xl bg-[#0B0D10] text-[#C8FF4D] font-display font-extrabold grid place-items-center shrink-0 text-[13px]">
                        {(b.business || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-display font-bold text-[15.5px] tracking-[-0.01em] truncate">{b.business}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-0.5 rounded-full border ${STATUS[b.status]?.cls || ""}`}>{STATUS[b.status]?.label || b.status}</span>
                          {b.source === "sales_call" && <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-[#6474FF]/12 text-[#4353E8]">15 min</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[12.5px] text-[#3A3D44]/75">
                          <span className="text-[#0B0D10] font-medium">{b.name}</span>
                          <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 hover:text-[#0B0D10] transition-colors"><Mail size={11} />{b.email}</a>
                          {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-[#0B0D10] transition-colors"><Phone size={11} />{b.phone}</a>}
                        </div>
                        {b.notes && <div className="mt-2 text-[12.5px] text-[#3A3D44] bg-[#F4F1E9] rounded-lg px-3 py-1.5 border-l-2 border-[#C8FF4D]">{b.notes}</div>}
                        {b.booked_by && <div className="mt-1.5 text-[11px] text-[#3A3D44]/50">Booked by <span className="font-semibold text-[#3A3D44]">{b.booked_by}</span></div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[13px] font-semibold text-[#0B0D10] inline-flex items-center gap-1.5"><Clock size={12} className="text-[#3A3D44]/50" />{b.slot_label?.replace(" AEDT", "") || "—"}</div>
                        {b.zoom_url && (
                          <a href={b.zoom_url} target="_blank" rel="noreferrer" data-testid={`meeting-join-${b.id}`}
                            className="btn-lift mt-2 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-[#0B0D10] text-white text-[11.5px] font-semibold">
                            <ExternalLink size={11} />Join call
                          </a>
                        )}
                      </div>
                    </div>
                    {b.status === "confirmed" && (
                      <div className="flex items-center gap-1.5 mt-4 pt-3.5 border-t border-[#F0EDE5]">
                        <button data-testid={`meeting-complete-${b.id}`} onClick={() => setStatus(b, "completed")}
                          className="btn-lift h-8 px-3.5 rounded-full bg-[#C8FF4D] text-[#0B0D10] text-[12px] font-bold inline-flex items-center gap-1"><Check size={12} strokeWidth={2.8} />Done</button>
                        <button data-testid={`meeting-no-show-${b.id}`} onClick={() => setStatus(b, "no_show")}
                          className="h-8 px-3.5 rounded-full bg-white border border-[#E4E0D6] text-[#3A3D44] text-[12px] font-medium hover:border-[#0B0D10]/40 transition-colors">No-show</button>
                        <button data-testid={`meeting-cancel-${b.id}`} onClick={() => setStatus(b, "cancelled")}
                          className="h-8 w-8 rounded-full bg-white border border-[#E4E0D6] text-[#3A3D44]/70 grid place-items-center hover:border-[#0B0D10]/40 hover:text-[#0B0D10] transition-colors" title="Cancel"><X size={13} /></button>
                      </div>
                    )}
                    {b.status !== "confirmed" && (
                      <div className="mt-4 pt-3.5 border-t border-[#F0EDE5]">
                        <button data-testid={`meeting-reopen-${b.id}`} onClick={() => setStatus(b, "confirmed")}
                          className="h-8 px-3.5 rounded-full bg-white border border-[#E4E0D6] text-[#3A3D44] text-[12px] font-medium hover:border-[#0B0D10]/40 transition-colors">Reopen</button>
                      </div>
                    )}
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
